import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Alert, Box, Button, Code, Stack, Title } from '@mantine/core';
import { IconAlertTriangle, IconRefresh } from '@tabler/icons-react';

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

/**
 * Глобальный error-boundary для маршрутов кабинета. Без него любая ошибка
 * в render-фазе React уносит всё SPA в белый экран.
 *
 * Отображает ошибку + stack trace + кнопку перезагрузки. Stack trace
 * нужен админу/разработчику, поэтому показываем всем; на проде это
 * допустимо (UI-ошибки не содержат секретов).
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null, errorInfo: null };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // eslint-disable-next-line no-console
    console.error('ErrorBoundary caught:', error, errorInfo);
    this.setState({ errorInfo });
  }

  reset = (): void => {
    this.setState({ error: null, errorInfo: null });
  };

  render(): ReactNode {
    if (this.state.error) {
      return (
        <Box p="md" maw={900} mx="auto">
          <Alert
            icon={<IconAlertTriangle size={20} />}
            color="red"
            title="Что-то сломалось"
            mb="md"
          >
            <Stack gap="xs">
              <div>
                <strong>{this.state.error.name}:</strong> {this.state.error.message}
              </div>
              <div>
                <Button
                  size="xs"
                  variant="white"
                  leftSection={<IconRefresh size={14} />}
                  onClick={this.reset}
                >
                  Попробовать снова
                </Button>
                {' '}
                <Button
                  size="xs"
                  variant="white"
                  onClick={() => window.location.reload()}
                >
                  Перезагрузить страницу
                </Button>
              </div>
            </Stack>
          </Alert>

          <Title order={6} mt="lg" mb="xs">
            Stack trace
          </Title>
          <Code block style={{ whiteSpace: 'pre-wrap', maxHeight: 400, overflow: 'auto' }}>
            {this.state.error.stack ?? '(нет stack)'}
          </Code>

          {this.state.errorInfo?.componentStack && (
            <>
              <Title order={6} mt="lg" mb="xs">
                Component stack
              </Title>
              <Code block style={{ whiteSpace: 'pre-wrap', maxHeight: 300, overflow: 'auto' }}>
                {this.state.errorInfo.componentStack}
              </Code>
            </>
          )}
        </Box>
      );
    }
    return this.props.children;
  }
}
